import _map from 'lodash/map';
import _round from 'lodash/round';
import dayjs from './dayjs';
import {
  getLabelIds,
  getToolIds,
  constants,
  compareWithNameTiebreaker,
} from '@macmillan-learning/gradebook-shared';
import { getIntegrationHubUrl } from './config';

const { NGA_SUBTYPES, RESOURCE_TYPE_FILTER } = constants;
const { TOOL, LABEL, SUBTYPE } = RESOURCE_TYPE_FILTER;

export function sortAssignmentsByISO(
  assignments,
  sortKey = 'assignment_due_at',
) {
  const sortedAssignments = assignments.sort((a, b) =>
    compareWithNameTiebreaker(dayjs(a[sortKey]).diff(dayjs(b[sortKey])), a, b),
  );
  return sortedAssignments;
}

export const emptyAlert = {
  show: false,
  type: '',
  text: '',
  dismissTime: 0,
  dismissible: true,
  focusOnShow: false,
  focusDelay: 0,
};

export const createGradebookAlert = ({
  type,
  text,
  dismissible = true,
  dismissTime = 0,
  focusOnShow = false,
  focusDelay = 0,
  ...overrides
}) => ({
  show: true,
  type,
  text,
  dismissible,
  dismissTime,
  focusOnShow,
  focusDelay,
  ...overrides,
});

export const isStudentAssignedToAssignment = (
  student,
  assignment,
  exceptions = [],
) => {
  let isAssigned = false;
  let studentAssignmentException;
  const { studentId } = student;
  const { id, assignment_due_at: dueDate } = assignment;

  if (Array.isArray(exceptions)) {
    studentAssignmentException = exceptions.find(
      (ex) =>
        ex.assignment_id === id &&
        ex.exception_type === 'assignment' &&
        ex.user_ids.includes(studentId),
    );
  }

  if (dueDate || studentAssignmentException) {
    // If is assigned to everyone or assigned to this student, isAssigned is true
    isAssigned = true;
  }

  return isAssigned;
};

export const regex = {
  email: (email) => {
    const emailRegex =
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line max-len
    return emailRegex.test(email);
  },
};

export const round = (value, decimals, ignoreDecimalZeros) => {
  const rounded = _round(value, decimals);
  if (ignoreDecimalZeros) return rounded;
  return rounded.toFixed(decimals);
};

export const getGradebookCategoryOptionsList = (
  categories,
  isUngraded = false,
) => {
  if (categories && categories.length) {
    const mappedCategories = _map(categories, (cat) => ({
      value: parseInt(cat.id, 10),
      label: cat.name,
      isDefault: cat.is_default,
      isDefaultUngraded: cat.is_default_ungraded,
    }));
    if (isUngraded) {
      return [mappedCategories.find((category) => category.isDefaultUngraded)];
    }
    return mappedCategories.filter((category) => !category.isDefaultUngraded);
  }
  return [];
};

export const updateSearchParamsInBrowser = (searchParams) => {
  const newRelativePathQuery = `${
    window.location.pathname
  }?${searchParams.toString()}`;
  window.history.pushState(null, '', newRelativePathQuery);
};

export const getAllResourceTypeFilterOptions = () => {
  const toolIds = getToolIds();
  const labelIds = getLabelIds();

  const filterOptions = [
    {
      label: 'Adaptive Quiz',
      filters: [{ type: TOOL, value: toolIds.learningcurve }],
    },
    {
      label: 'Assessment',
      filters: [{ type: TOOL, value: toolIds.assessment }],
    },
    {
      label: 'Diagnostic',
      filters: [
        { type: TOOL, value: toolIds.pathfinder },
        { type: TOOL, value: toolIds.pathfinder2 },
      ],
    },
    {
      label: 'File',
      filters: [
        { type: TOOL, value: toolIds.staticfile },
        { type: TOOL, value: toolIds.instructorfile },
      ],
    },
    {
      label: 'Goal-Setting and Reflection Survey',
      filters: [{ type: LABEL, value: labelIds.grs }],
    },
    {
      label: 'Guided Reading',
      filters: [{ type: SUBTYPE, value: NGA_SUBTYPES.GUIDED_READING }],
    },
    {
      label: 'iClicker',
      filters: [
        { type: TOOL, value: toolIds.iclicker },
        { type: TOOL, value: toolIds.reef },
      ],
    },
    {
      label: 'Instructional Video and Video Activity',
      filters: [{ type: LABEL, value: labelIds.instructionalvideo }],
    },
    {
      label: 'Instructor Page',
      filters: [{ type: TOOL, value: toolIds['ccw-pages'] }],
    },
    {
      label: 'Instructor Resource',
      filters: [{ type: LABEL, value: labelIds.instructorresource }],
    },
    { label: 'Interactive', filters: [{ type: TOOL, value: toolIds.mapi }] },
    { label: 'Link', filters: [{ type: TOOL, value: toolIds.url }] },
    {
      label: 'Read & Practice',
      filters: [
        { type: TOOL, value: toolIds.learning_curve_read_and_practice },
        { type: TOOL, value: toolIds.readandpractice },
      ],
    },
    { label: 'Reading', filters: [{ type: TOOL, value: toolIds.reading }] },
    {
      label: 'Short-Form Writing',
      filters: [{ type: SUBTYPE, value: NGA_SUBTYPES.SHORT_FORM_WRITING }],
    },
    {
      label: 'Writing Assignment',
      filters: [{ type: TOOL, value: toolIds.writing }],
    },
  ];

  return filterOptions;
};

export const isCurrentUserPreviewingCourse = (courseId) => {
  const previewCourses = JSON.parse(localStorage.getItem('userPreview')) || [];
  return previewCourses.includes(courseId);
};

export const getHubLaunchUrl = ({ lmsId, courseId, resourceId }) => {
  const hubUrl = getIntegrationHubUrl();
  const queryStringParams = new URLSearchParams({
    fromCourseArea: 'gradebook',
    actualCourseId: courseId,
    isStudentPreview: isCurrentUserPreviewingCourse(courseId),
    isNew: false,
  });
  return `${hubUrl}/launch/${lmsId}/${courseId}/${resourceId}?${queryStringParams.toString()}`;
};

export const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
